import { Trans, useLingui } from "@lingui/react";
import { WIcon } from "@mobility-item-page/client";
import { formatBrand, toDateInputValue } from "@mobility-item-page/utilities";
import { DatePicker } from "@warp-ds/elements/react/datepicker";
import { Affix, Alert, Button, Modal, TextArea, TextField, Toggle } from "@warp-ds/react";
import { addDays, startOfDay } from "date-fns";
import { type SubmitEvent, useCallback, useEffect, useId, useMemo, useRef, useState } from "react";
import { useValidDealerItemContext } from "../../context/DealerItemProviderContext.tsx";
import { useTracking } from "../../utils/pulseHelper.ts";
import { validatePhone } from "../../utils/validatePhone.ts";
import { openContactFormEventId } from "./ContactFormModal.tsx";
import { MinimizedModal } from "./MinimizedModal.tsx";

enum ContactFormState {
  Initial,
  Submitting,
  Success,
  InvalidEid,
  InvalidLogin,
  Error,
}

enum DisplayState {
  Hidden,
  Minimized,
  Open,
}

type BoxStates = {
  callMeBack: boolean;
  bookATestDrive: boolean;
  offerFinance: boolean;
  tradeInCar: boolean;
};

const empty = (message: string) => message.trim().length === 0;

export const ContactFormModalv3 = () => {
  const [display, setDisplay] = useState<DisplayState>(DisplayState.Hidden);
  const [formState, setFormState] = useState<ContactFormState>(ContactFormState.Initial);
  const [phoneNumber, setPhoneNumber] = useState("");
  const [testDriveDate, setTestDriveDate] = useState<string>("");
  const [licencePlate, setLicencePlate] = useState("");
  const [distanceDriven, setDistanceDriven] = useState("");
  const [message, setMessage] = useState("");
  const [errorCode, setErrorCode] = useState(0);
  const [boxStates, setBoxStates] = useState<BoxStates>({
    callMeBack: false,
    bookATestDrive: false,
    offerFinance: false,
    tradeInCar: false,
  } satisfies Record<keyof BoxStates, false>);
  type FieldErrors = {
    message: string;
    phone: string;
    testDriveDate: string;
    licencePlate: string;
    distanceDriven: string;
  };
  const emptyFieldErrors: FieldErrors = useMemo(
    () => ({ message: "", phone: "", testDriveDate: "", licencePlate: "", distanceDriven: "" }),
    [],
  );
  const [fieldErrors, setFieldErrors] = useState<FieldErrors>(emptyFieldErrors);
  const clearFieldErrors = useCallback(() => setFieldErrors(emptyFieldErrors), [emptyFieldErrors]);
  const setFieldError = (field: keyof FieldErrors, value: string) =>
    setFieldErrors((prev) => ({ ...prev, [field]: value }));
  const formId = useId();
  const today = startOfDay(new Date());
  const maxDate = addDays(today, 30);
  const phoneNumberRef = useRef<HTMLInputElement>(null);
  const licencePlateRef = useRef<HTMLInputElement>(null);
  const { i18n } = useLingui();
  const {
    trackViewContactFormModal,
    trackCancelContactFormModal,
    trackSubmitContactForm,
    trackMinimizeContactFormModal,
    trackSentMessageWithCheckboxes,
  } = useTracking();

  const {
    organisationData: { name },
    itemData: { adId },
    external: { isMobile, brand, locale, adViewType },
  } = useValidDealerItemContext();

  const triggerCloseOrMinimize = useCallback(() => {
    if ((message !== "" || Object.values(boxStates).some(Boolean)) && !isMobile && display === DisplayState.Open) {
      setDisplay(DisplayState.Minimized);
      trackMinimizeContactFormModal();
    } else {
      setDisplay(DisplayState.Hidden);
      if (formState !== ContactFormState.Success) {
        trackCancelContactFormModal();
      }
    }
  }, [message, isMobile, display, trackCancelContactFormModal, trackMinimizeContactFormModal, formState, boxStates]);

  useEffect(() => {
    const listener = () => {
      setDisplay(DisplayState.Open);
      trackViewContactFormModal({ isSuccessView: false, adViewType });
    };
    const escListener = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        triggerCloseOrMinimize();
        clearFieldErrors();
        setFormState(ContactFormState.Initial);
      }
    };

    if (formState === ContactFormState.Success) {
      trackViewContactFormModal({ isSuccessView: true, adViewType });
    }

    window.addEventListener(openContactFormEventId, listener);
    window.addEventListener("keydown", escListener);
    return () => {
      window.removeEventListener(openContactFormEventId, listener);
      window.removeEventListener("keydown", escListener);
    };
  }, [trackViewContactFormModal, formState, triggerCloseOrMinimize, adViewType, clearFieldErrors]);

  const title = i18n.t({
    id: "dealer.contactForm.writeToSeller.title",
    message: "Write to {dealerName}",
    values: { dealerName: name },
    comment: "Header for the contact form",
  });

  const errorText = i18n.t({
    id: "dealer.contactForm.errorText.generic",
    message: "An error occurred while sending your message.\n" + "Please try again later. (Error code: {errorCode})",
    values: { errorCode },
    comment:
      "Generic error text displayed when an unexpected error occurs while submitting the contact form, includes error code",
  });

  const mandatoryFieldText = () =>
    i18n.t({
      id: "dealer.contactForm.textArea.helpText.mandatory",
      message: "Must be filled out",
      comment: "Help text when field has changed and has no value, displayed underneath the text-area",
    });

  const validateMessage = (required: boolean): boolean => {
    if (!message) {
      if (required) {
        setFieldError(
          "message",
          i18n.t({
            id: "dealer.contactForm.textArea.helpText.mandatoryOrChooseOptions",
            message: "Write a message or choose what you want to contact the dealer about.",
            comment: "Help text when the message is empty, displayed underneath the text-area",
          }),
        );
        return false;
      }
      setFieldError("message", "");
      return true;
    }
    if (empty(message)) {
      setFieldError(
        "message",
        i18n.t({
          id: "dealer.contactForm.textArea.helpText.emptyText",
          message: "Enter a message for the seller",
          comment:
            "Help text when the field has changed and is empty except spaces, displayed underneath the text-area",
        }),
      );
      setMessage("");
      return false;
    }
    setFieldError("message", "");
    return true;
  };

  const validatePhoneNo = (): boolean => {
    if (!validatePhone(phoneNumber, brand)) {
      setFieldError(
        "phone",
        i18n.t({
          id: "dealer.contactForm.phone.helpText.invalid",
          message: "Enter a valid phone number",
          comment: "Help text when the phone number format is invalid, displayed underneath the phone number field",
        }),
      );
      return false;
    }
    setFieldError("phone", "");
    return true;
  };

  const validateTestDriveDate = (dateValue?: string): boolean => {
    const value = dateValue ?? testDriveDate;
    if (!value || empty(value)) {
      setFieldError(
        "testDriveDate",
        i18n.t({
          id: "dealer.contactForm.testDriveDate.required",
          message: "Enter a desired date for a test drive",
        }),
      );
      return false;
    }

    const selectedDate = startOfDay(value);

    // Check if the date is valid (not NaN)
    if (Number.isNaN(selectedDate.getTime())) {
      setFieldError(
        "testDriveDate",
        i18n.t({
          id: "dealer.contactForm.testDriveDate.helpText.invalidDate",
          message: "Enter a valid date",
          comment: "Help text when the entered test drive date is not a valid date format",
        }),
      );
      return false;
    }

    if (selectedDate < today) {
      setFieldError(
        "testDriveDate",
        i18n.t({
          id: "dealer.contactForm.testDriveDate.helpText.pastDate",
          message: "Date cannot be in the past",
          comment: "Help text when the selected test drive date is before today",
        }),
      );
      return false;
    }

    if (selectedDate > maxDate) {
      setFieldError(
        "testDriveDate",
        i18n.t({
          id: "dealer.contactForm.testDriveDate.helpText.tooFar",
          message: "Date must be within 30 days",
          comment: "Help text when the selected test drive date is more than 30 days away",
        }),
      );
      return false;
    }

    setFieldError("testDriveDate", "");
    return true;
  };

  const validateLicencePlate = (): boolean => {
    return validateRequired(
      "licencePlate",
      licencePlate,
      i18n.t({
        id: "dealer.contactForm.licenceplate.helpText.required",
        message: "Enter licence plate",
        comment: "Help text when the licence plate field is empty",
      }),
    );
  };

  const validateDistanceDriven = (): boolean => {
    const isPresent = validateRequired(
      "distanceDriven",
      distanceDriven,
      i18n.t({
        id: "dealer.contactForm.distanceDriven.helpText.required",
        message: "Enter distance driven",
        comment: "Help text when the distance driven field is empty",
      }),
    );
    if (!isPresent) return false;

    if (/[^0-9\s.,]/.test(distanceDriven)) {
      setFieldError(
        "distanceDriven",
        i18n.t({
          id: "dealer.contactForm.distanceDriven.helpText.numericOnly",
          message: "Distance driven must be a number",
          comment: "Help text when the distance driven field contains non-numeric characters",
        }),
      );
      return false;
    }
    setFieldError("distanceDriven", "");
    return true;
  };

  const handleMessageChange = (newMessage: string) => {
    setFormState(ContactFormState.Initial);
    setFieldError("message", "");
    setMessage(newMessage);
  };

  const handlePhoneChange = (newPhone: string) => {
    setFormState(ContactFormState.Initial);
    setFieldError("phone", "");
    setPhoneNumber(newPhone.trim().replace(/\s/g, ""));
  };

  const flipBoxState = (state: keyof BoxStates, checked: boolean) => {
    setBoxStates((prevState) => ({ ...prevState, [state]: checked }));
  };

  useEffect(() => {
    if (boxStates.callMeBack) {
      phoneNumberRef.current?.focus();
    }
  }, [boxStates.callMeBack]);

  useEffect(() => {
    if (boxStates.tradeInCar) {
      licencePlateRef.current?.focus();
    }
  }, [boxStates.tradeInCar]);

  const validateRequired = (field: keyof FieldErrors, value: string, errorMessage?: string): boolean => {
    if (!value || empty(value)) {
      setFieldError(field, errorMessage ?? mandatoryFieldText());
      return false;
    }
    setFieldError(field, "");
    return true;
  };

  const validate = () => {
    const validPhone = boxStates.callMeBack ? validatePhoneNo() : true;
    const validTestDriveDate = boxStates.bookATestDrive ? validateTestDriveDate() : true;
    const validLicencePlate = boxStates.tradeInCar ? validateLicencePlate() : true;
    const validDistanceDriven = boxStates.tradeInCar ? validateDistanceDriven() : true;

    const hasValidCheckedOption =
      (boxStates.callMeBack && validPhone) ||
      (boxStates.bookATestDrive && validTestDriveDate) ||
      boxStates.offerFinance ||
      (boxStates.tradeInCar && validLicencePlate && validDistanceDriven);

    const validMessage = validateMessage(!hasValidCheckedOption);

    return [validMessage, validPhone, validTestDriveDate, validLicencePlate, validDistanceDriven].every(Boolean);
  };

  const handleSubmit = async (event: SubmitEvent) => {
    event.preventDefault();
    const isValid = validate();

    if (!isValid) {
      return;
    } else {
      setFormState(ContactFormState.Submitting);
    }

    const messageParts: string[] = [message];

    if (boxStates.callMeBack) {
      messageParts.push(
        i18n.t({
          id: "dealer.contactForm.resolvedMessage.callMeBack",
          message: "I would like to be called back.",
          comment: "Appended to the message when the user has selected the call me back option",
        }),
      );
    }

    if (boxStates.offerFinance) {
      messageParts.push(
        i18n.t({
          id: "dealer.contactForm.resolvedMessage.offerFinance",
          message: "I would like to receive a financing offer.",
          comment: "Appended to the message when the user has selected the financing offer option",
        }),
      );
    }

    if (boxStates.tradeInCar) {
      messageParts.push(
        i18n.t({
          id: "dealer.contactForm.resolvedMessage.tradeInCar",
          message: "I would like a trade-in price on my car: reg. no. {licencePlate}, kilometres {distanceDriven}.",
          values: { licencePlate, distanceDriven: distanceDriven },
          comment:
            "Appended to the message when the user has selected the trade-in car option, includes licence plate and distance driven",
        }),
      );
    }

    if (boxStates.bookATestDrive) {
      messageParts.push(
        i18n.t({
          id: "dealer.contactForm.resolvedMessage.bookATestDrive",
          message: "I would like to book a test drive on {testDriveDate}.",
          values: {
            testDriveDate: new Date(testDriveDate).toLocaleDateString(locale, {
              weekday: "long",
              month: "long",
              day: "numeric",
            }),
          },
          comment:
            "Appended to the message when the user has selected the book a test drive option, includes the requested date",
        }),
      );
    }

    const resolvedMessage = messageParts.join("\r\n");

    try {
      const response = await fetch(`/mobility/api/contact/${adId}`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          message: resolvedMessage,
          phoneNumber: boxStates.callMeBack ? phoneNumber : "",
        }),
      });

      if (!response.ok) {
        const data = await response.json();
        if (response.status === 401) {
          if (data.message === "Not logged in") {
            setFormState(ContactFormState.InvalidLogin);
            return;
          } else if (data.message === "eID verification failed") {
            setFormState(ContactFormState.InvalidEid);
            return;
          } else {
            setErrorCode(response.status);
            throw new Error(`Failed to send message: ${response.status} ${response.statusText} ${data.message}`);
          }
        } else {
          setErrorCode(response.status);
          throw new Error(`Failed to send message: ${response.status} ${response.statusText} ${data.message}`);
        }
      }

      trackSubmitContactForm(phoneNumber);
      trackSentMessageWithCheckboxes(
        message !== "",
        boxStates.callMeBack,
        boxStates.bookATestDrive,
        boxStates.offerFinance,
        boxStates.tradeInCar,
      );
      // Success - reset form and show success view
      resetForm();
      setFormState(ContactFormState.Success);
    } catch (error) {
      console.error("Error sending contact form:", error);
      setFormState(ContactFormState.Error);
    }
  };

  const resetForm = () => {
    setMessage("");
    setPhoneNumber("");
    setTestDriveDate("");
    setLicencePlate("");
    setDistanceDriven("");
    setBoxStates({
      callMeBack: false,
      bookATestDrive: false,
      offerFinance: false,
      tradeInCar: false,
    });
  };

  return (
    <>
      <Modal
        className="[&>div>div]:items-center"
        open={display === DisplayState.Open}
        onDismiss={() => {
          triggerCloseOrMinimize();
          clearFieldErrors();
          setFormState(ContactFormState.Initial);
        }}
        title={title}
        right={
          <Button
            pill
            aria-label={
              [
                message !== "",
                boxStates.callMeBack,
                boxStates.bookATestDrive,
                boxStates.tradeInCar,
                boxStates.offerFinance,
              ].includes(true) &&
              !isMobile &&
              display === DisplayState.Open
                ? i18n.t({
                    id: "dealer.modal.minimize.button",
                    message: "Minimize",
                    comment: "Aria label for the minimize button in modal",
                  })
                : i18n.t({
                    id: "dealer.modal.close.button",
                    message: "Close",
                    comment: "Aria label for the close button in modal",
                  })
            }
            onClick={() => {
              triggerCloseOrMinimize();
              clearFieldErrors();
              setFormState(ContactFormState.Initial);
            }}
            disabled={formState === ContactFormState.Submitting}
            className="-mr-8 sm:-mr-12"
          >
            {[
              message !== "",
              boxStates.callMeBack,
              boxStates.bookATestDrive,
              boxStates.tradeInCar,
              boxStates.offerFinance,
            ].includes(true) &&
            !isMobile &&
            display === DisplayState.Open ? (
              <WIcon name="Minus" size="24px" />
            ) : (
              <WIcon name="Close" size="24px" />
            )}
          </Button>
        }
        footer={
          formState !== ContactFormState.Success ? (
            <Button
              primary
              type="submit"
              form={formId}
              loading={formState === ContactFormState.Submitting}
              fullWidth={isMobile}
            >
              <Trans id="dealer.contactForm.submitButton.text" message="Send message" />
            </Button>
          ) : (
            <Button
              fullWidth={isMobile}
              className="self-end"
              onClick={() => {
                setDisplay(DisplayState.Hidden);
                setFormState(ContactFormState.Initial);
              }}
            >
              <Trans id="dealer.modal.close.button" message="Close" />
            </Button>
          )
        }
      >
        {formState !== ContactFormState.Success ? (
          <form id={formId} onSubmit={handleSubmit} noValidate>
            <fieldset className="overflow-hidden">
              <legend className="antialiased block relative h5 pb-4 cursor-pointer s-text mb-16">
                <Trans id="dealer.contactForm.introText" message="I would like to..." />
              </legend>
              <Toggle
                className="mb-16"
                type="checkbox"
                checked={boxStates.callMeBack}
                onChange={(checked) => flipBoxState("callMeBack", checked)}
                label={i18n.t({ id: "dealer.contactForm.request.callMe.label", message: "Be called" })}
              />
              {boxStates.callMeBack && (
                <TextField
                  label={i18n.t({
                    id: "dealer.contactForm.phone.label",
                    message: "Phone number",
                    comment: "Label for the phone number text field",
                  })}
                  placeholder={i18n.t({
                    id: "dealer.contactForm.phone.placeholder",
                    message: "Enter your phone number",
                    comment:
                      "Placeholder for the phone number field, using the country code for each country. Only the default (english) is a text",
                  })}
                  type="tel"
                  disabled={formState === ContactFormState.Submitting}
                  invalid={fieldErrors.phone !== ""}
                  helpText={fieldErrors.phone}
                  aria-errormessage={fieldErrors.phone !== "" ? fieldErrors.phone : undefined}
                  ref={phoneNumberRef}
                  value={phoneNumber}
                  onChange={({ target: { value } }) => handlePhoneChange(value)}
                  onBlur={() => validatePhoneNo()}
                  className="mb-16"
                />
              )}
              {adViewType && ["car-used", "car-new", "car-leasing"].includes(adViewType) && (
                <>
                  <Toggle
                    className="mb-16"
                    type="checkbox"
                    checked={boxStates.bookATestDrive}
                    onChange={(checked) => flipBoxState("bookATestDrive", checked)}
                    label={i18n.t({ id: "dealer.contactForm.request.testdrive.label", message: "Book a testdrive" })}
                  />
                  {boxStates.bookATestDrive && (
                    <div>
                      {isMobile ? (
                        <div className="overflow-hidden max-w-full">
                          <label className="block mb-4 max-w-full">
                            <span className="text-s s-text font-bold">
                              {i18n.t({ id: "dealer.contactForm.request.testdrive.date", message: "Requested date" })}
                            </span>
                            <input
                              type="date"
                              name="testDriveDate"
                              min={toDateInputValue(today, locale)}
                              max={toDateInputValue(maxDate, locale)}
                              value={testDriveDate}
                              onChange={(e) => {
                                setTestDriveDate(e.target.value);
                                setFieldError("testDriveDate", "");
                              }}
                              onBlur={() => {
                                if (testDriveDate) validateTestDriveDate();
                              }}
                              className={`block appearance-none [-webkit-appearance:none] box-border min-w-0 max-w-full min-h-[40px] text-m mb-0 px-12 py-8 rounded-4 w-full focusable focus:[--w-outline-offset:-2px] caret-current border-1 s-text s-bg hover:s-border-strong-hover active:s-border-selected ${
                                fieldErrors.testDriveDate ? "s-border-negative" : "s-border-strong"
                              }`}
                            />
                          </label>
                          {fieldErrors.testDriveDate && (
                            <div className="mt-4 text-xs s-text-negative">{fieldErrors.testDriveDate}</div>
                          )}
                        </div>
                      ) : (
                        <div
                          className={
                            fieldErrors.testDriveDate
                              ? "[&_input]:s-border-negative hover:[&_input]:s-border-negative-hover"
                              : ""
                          }
                        >
                          <DatePicker
                            label={i18n.t({
                              id: "dealer.contactForm.request.testdrive.date",
                              message: "Requested date",
                            })}
                            isDayDisabled={(day) => day < today || day > maxDate}
                            onBlur={(e: Event) => {
                              const selectedDate = (e.target as HTMLInputElement).value;
                              setTestDriveDate(selectedDate);
                              if (selectedDate) {
                                validateTestDriveDate(selectedDate);
                              } else {
                                setFieldError("testDriveDate", "");
                              }
                            }}
                            value={testDriveDate}
                          />
                          {fieldErrors.testDriveDate && (
                            <div className="mt-4 text-xs s-text-negative">{fieldErrors.testDriveDate}</div>
                          )}
                        </div>
                      )}
                      <div className="mb-16 mt-4 text-xs s-text-subtle">
                        <Trans
                          id="dealer.contactForm.request.testdrive.infoText"
                          message="Note that this is a request - The dealer will contact you for an exact time/date for the testdrive"
                        />
                      </div>
                    </div>
                  )}
                </>
              )}
              {adViewType && ["car-used", "car-new"].includes(adViewType) && (
                <>
                  <Toggle
                    className="mb-16"
                    type="checkbox"
                    checked={boxStates.offerFinance}
                    onChange={(checked) => flipBoxState("offerFinance", checked)}
                    label={i18n.t({
                      id: "dealer.contactForm.request.finance.label",
                      message: "Receive a financing offer",
                    })}
                  />
                  <Toggle
                    className="mb-16"
                    type="checkbox"
                    checked={boxStates.tradeInCar}
                    onChange={(checked) => flipBoxState("tradeInCar", checked)}
                    label={i18n.t({
                      id: "dealer.contactForm.request.tradeInCar.label",
                      message: "Receive a trade-in price for my car",
                    })}
                  />
                  {boxStates.tradeInCar && (
                    <div className="flex gap-16 mb-16">
                      <TextField
                        label={i18n.t({
                          id: "dealer.contactForm.licenceplate.label",
                          message: "Licence plate",
                          comment: "Label for the licence plate input field",
                        })}
                        placeholder={i18n.t({
                          id: "dealer.contactForm.licenceplate.placeholder",
                          message: "XX XX XXX",
                          comment:
                            "Placeholder for the licence plate input field - Should contain X's to indicate the format of the licence plate in the relevant country",
                        })}
                        type="text"
                        className="w-[296px]"
                        invalid={fieldErrors.licencePlate !== ""}
                        helpText={fieldErrors.licencePlate}
                        aria-errormessage={fieldErrors.licencePlate !== "" ? fieldErrors.licencePlate : undefined}
                        value={licencePlate}
                        ref={licencePlateRef}
                        onChange={({ target: { value } }) => {
                          setLicencePlate(value);
                          setFieldError("licencePlate", "");
                        }}
                        onBlur={() => validateLicencePlate()}
                      />
                      <TextField
                        label={i18n.t({
                          id: "dealer.contactForm.distanceDriven.label",
                          message: "Distance driven",
                          comment: "Label for the licence plate input field",
                        })}
                        placeholder={i18n.t({
                          id: "dealer.contactForm.distanceDriven.placeholder",
                          message: "fx 60.000",
                          comment:
                            "Placeholder for the distance driven field. Should represent a number that matches the standard measurement unit for the relevant country",
                        })}
                        type="text"
                        className="w-[296px]"
                        invalid={fieldErrors.distanceDriven !== ""}
                        helpText={fieldErrors.distanceDriven}
                        aria-errormessage={fieldErrors.distanceDriven !== "" ? fieldErrors.distanceDriven : undefined}
                        value={distanceDriven}
                        onChange={({ target: { value } }) => {
                          setDistanceDriven(value);
                          setFieldError("distanceDriven", "");
                        }}
                        onBlur={() => validateDistanceDriven()}
                      >
                        <Affix
                          suffix
                          label={i18n.t({
                            id: "dealer.contactForm.distanceDriven.suffix.label",
                            message: "km",
                            comment:
                              "Label for the suffix in the distance driven input field, should match the standard measurement unit for the relevant country (km for nb and da, mil for dv)",
                          })}
                        />
                      </TextField>
                    </div>
                  )}
                </>
              )}
            </fieldset>
            <TextArea
              label={i18n.t({
                id: "dealer.contactForm.textArea.label",
                message: "Message to the seller",
                comment: "Label for the message, explains that this text will be sent to the advertiser",
              })}
              placeholder={i18n.t({
                id: "dealer.contactForm.textArea.placeholder",
                message: "Write a message…",
                comment: "Placeholder for the message text area",
              })}
              value={message}
              disabled={formState === ContactFormState.Submitting}
              invalid={fieldErrors.message !== ""}
              helpText={fieldErrors.message}
              aria-errormessage={fieldErrors.message !== "" ? fieldErrors.message : undefined}
              minimumRows={5}
              className="mb-8"
              onChange={({ target: { value: newValue } }) => {
                handleMessageChange(newValue);
              }}
              onBlur={() => validateMessage(!Object.values(boxStates).some(Boolean))}
            />
            <div className="mb-8 text-xs s-text-subtle">
              <Trans
                id="dealer.contactForm.infoText.dynamic"
                message="When you send this message, your name{hasPhoneNumber, select, yes {, e-mail and phone number} other { and e-mail}} are transferred to the seller, who will then be responsible for your data. The message is sent via your registered email. {brand} may review and block messages to prevent fraud."
                values={{
                  brand: formatBrand(brand),
                  hasPhoneNumber: phoneNumber ? "yes" : "no",
                }}
              />
              <div className="mt-8">
                <Button link className="text-xs" href="/my-page/my-account" target="_blank">
                  <Trans id="dealer.contactForm.profileLink" message="You can view and change your e-mail here" />
                </Button>
              </div>
            </div>
            <Alert show={formState === ContactFormState.InvalidLogin} type="negative" className="mb-24">
              <h2 className="t4">
                <Trans
                  id="dealer.contactForm.loginErrorText.title"
                  message="You have been logged out"
                  comment="Alert title shown on invalid login error"
                />
              </h2>
              <p>
                <Trans
                  id="dealer.contactForm.loginErrorText.description"
                  message="It looks like your session has expired. Log in again to send your message to the seller."
                  comment="Alert description displayed when a user was unauthorised while submitting the contact form"
                />
              </p>
              <Button className="flex items-center gap-8" href="/auth/login" target="_blank">
                <Trans id="dealer.contactForm.loginErrorButton.text" message="Log in (opens in new window)" />
                <WIcon name="LinkExternal" />
              </Button>
            </Alert>
            <Alert show={formState === ContactFormState.InvalidEid} type="negative" className="mb-24">
              <h2 className="t4">
                <Trans
                  id="dealer.eidModal.title"
                  message="Verify your identity"
                  comment="Alert title shown when eID verification is required or has expired"
                />
              </h2>
              <p>
                <Trans
                  id="dealer.eidModal.description.sendMessage"
                  message="Before contacting someone, you need to verify your account. This lets the person on the other end feel confident about who they are talking to."
                  comment="Alert description displayed when eID verification is required to submit the contact form"
                />
              </p>
              <Button className="flex items-center gap-8" href="/auth/login/eid" target="_blank">
                <Trans
                  id="dealer.eidModal.verifyButton"
                  message="Verify with Electronic ID"
                  comment="Text for button to go to the BankID / MitId validation page. The text should contain the correct validator for each country (eg. bankId for NB and SV, and MitID for DA)"
                />
                <WIcon name="LinkExternal" />
              </Button>
            </Alert>
            <Alert show={formState === ContactFormState.Error} type="negative" className="mb-24">
              {errorText}
            </Alert>
          </form>
        ) : (
          <div className="flex flex-col gap-24">
            <Trans
              id="dealer.contactForm.success.text"
              message="Your message has been sent. The dealer will contact you as soon as possible."
              comment="Message shown when contact form is sent successfully"
            />
          </div>
        )}
      </Modal>
      {display === DisplayState.Minimized && (
        <MinimizedModal
          dealerName={name}
          maximize={() => setDisplay(DisplayState.Open)}
          close={() => setDisplay(DisplayState.Hidden)}
        />
      )}
    </>
  );
};
